// Painters for the Square's living things. Each draws around its base point
// (0, 0) — where it touches the ground — upward into negative y, so the
// world can depth-sort it against walkers by that base.
import { rng, inkedCircles, clipCircles, hatch, inkPoly, inkEllipse, line, mix } from "./paint"

// A broadleaf tree: a tapered trunk and a canopy of overlapping lobes, lit
// from the upper left, with leaf texture and a pencil-hatched shadow side.
export function paintOak(ctx, pal, s, seed) {
    const r = rng(seed)
    const ink = pal.ink
    // trunk with root flare
    const tw = s * 0.2
    inkPoly(ctx, [
        [-tw * 1.5, 0], [-tw * 0.7, -s * 0.25], [-tw * 0.6, -s * 1.15],
        [tw * 0.6, -s * 1.15], [tw * 0.7, -s * 0.25], [tw * 1.5, 0]
    ], pal.trunk, ink, { line: 1.5 })
    line(ctx, [[tw * 0.25, -s * 0.2], [tw * 0.3, -s * 1.0]], pal.trunkDark, 1.6, 0.8)
    line(ctx, [[-tw * 0.2, -s * 0.5], [-tw * 0.1, -s * 0.8]], pal.trunkDark, 1, 0.5)

    const cy = -s * 1.6
    const lobes = []
    const n = 6 + Math.floor(r() * 3)
    for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + r() * 0.5
        const d = s * (0.38 + r() * 0.18)
        lobes.push([Math.cos(a) * d * 1.1, cy + Math.sin(a) * d * 0.8, s * (0.42 + r() * 0.2)])
    }
    lobes.push([0, cy - s * 0.1, s * 0.62])
    lobes.push([-s * 0.15, cy - s * 0.45, s * 0.45])

    inkedCircles(ctx, lobes, pal.leafDark, ink, 1.9)
    ctx.save()
    clipCircles(ctx, lobes)
    // mid tone lit from the upper left
    lobes.forEach(([x, y, rad]) => {
        ctx.fillStyle = pal.leaf
        ctx.beginPath(); ctx.arc(x - rad * 0.16, y - rad * 0.2, rad * 0.82, 0, Math.PI * 2); ctx.fill()
    })
    lobes.forEach(([x, y, rad]) => {
        if (x > s * 0.2 && y > cy) return
        ctx.fillStyle = pal.leafLight
        ctx.beginPath(); ctx.arc(x - rad * 0.3, y - rad * 0.38, rad * 0.42, 0, Math.PI * 2); ctx.fill()
    })
    // leaf texture: little crescents and dots
    for (let i = 0; i < s * 3.2; i++) {
        const x = (r() - 0.5) * s * 2.3, y = cy + (r() - 0.55) * s * 2
        const light = x + (y - cy) < 0
        ctx.strokeStyle = light ? mix(pal.leafLight, "#FFFFFF", 0.25) : pal.leafShadow
        ctx.globalAlpha = 0.55
        ctx.lineWidth = 1.1
        ctx.beginPath(); ctx.arc(x, y, 2.2 + r() * 1.8, Math.PI * 0.1, Math.PI * 0.9); ctx.stroke()
    }
    ctx.globalAlpha = 1
    // hatched shade on the lower right
    ctx.beginPath(); ctx.ellipse(s * 0.55, cy + s * 0.55, s * 1.1, s * 0.75, -0.5, 0, Math.PI * 2); ctx.clip()
    hatch(ctx, [-s * 1.3, cy - s * 1.2, s * 2.6, s * 2.4], pal.leafShadow, { seed, gap: 2.6, alpha: 0.55, width: 1.1 })
    ctx.restore()
    return { top: cy - s * 1.15, radius: s * 1.2 }
}

// A conifer: stacked jagged tiers, the sunlit left half lighter.
export function paintPine(ctx, pal, s, seed) {
    const r = rng(seed)
    const ink = pal.ink
    inkPoly(ctx, [[-s * 0.14, 0], [-s * 0.1, -s * 0.55], [s * 0.1, -s * 0.55], [s * 0.14, 0]], pal.trunk, ink, { line: 1.4 })
    const tiers = 4
    const height = s * 2.9
    for (let t = 0; t < tiers; t++) {
        const k = t / tiers
        const baseY = -s * 0.45 - k * height * 0.62
        const w = s * (1.05 - k * 0.72)
        const tip = baseY - height * 0.42
        const pts = [[0, tip]]
        const teeth = 5
        for (let i = 0; i <= teeth; i++) {
            const x = w - (i / teeth) * w * 2
            const y = baseY + (i % 2 ? -s * 0.08 : s * 0.04) + (r() - 0.5) * 3
            pts.push([x, y])
        }
        inkPoly(ctx, pts, pal.pine, ink, { line: 1.6 })
        ctx.save()
        ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
        ctx.fillStyle = pal.pineLight
        ctx.beginPath(); ctx.moveTo(0, tip); ctx.lineTo(-w * 1.1, baseY + 4); ctx.lineTo(-w * 0.05, baseY + 4); ctx.closePath(); ctx.fill()
        hatch(ctx, [0, tip, w, baseY - tip + 6], pal.pineDark, { seed: seed + t, angle: -1.1, gap: 2.4, alpha: 0.6 })
        ctx.restore()
    }
    return { top: -s * 0.45 - height * 0.62 * ((tiers - 1) / tiers) - height * 0.42, radius: s * 1.05 }
}

// A round shrub, sometimes with berries or flowers.
export function paintBush(ctx, pal, s, seed) {
    const r = rng(seed)
    const lobes = []
    const n = 4 + Math.floor(r() * 3)
    for (let i = 0; i < n; i++) {
        const x = (i / (n - 1) - 0.5) * s * 1.6
        lobes.push([x, -s * 0.45 - r() * s * 0.25, s * (0.38 + r() * 0.16)])
    }
    lobes.push([0, -s * 0.75, s * 0.45])
    inkedCircles(ctx, lobes, pal.leafDark, pal.ink, 1.7)
    ctx.save()
    clipCircles(ctx, lobes)
    lobes.forEach(([x, y, rad]) => {
        ctx.fillStyle = pal.leaf
        ctx.beginPath(); ctx.arc(x - rad * 0.2, y - rad * 0.25, rad * 0.78, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = pal.leafLight
        ctx.beginPath(); ctx.arc(x - rad * 0.35, y - rad * 0.42, rad * 0.35, 0, Math.PI * 2); ctx.fill()
    })
    ctx.restore()
    const dots = r() < 0.45 ? (r() < 0.5 ? pal.berry : pal.flowers[Math.floor(r() * pal.flowers.length)]) : null
    if (dots) {
        for (let i = 0; i < 7; i++) {
            const [x, y, rad] = lobes[Math.floor(r() * lobes.length)]
            ctx.fillStyle = dots
            ctx.beginPath(); ctx.arc(x + (r() - 0.5) * rad, y + (r() - 0.5) * rad, 2.1, 0, Math.PI * 2); ctx.fill()
        }
    }
}

export function paintRock(ctx, pal, s, seed) {
    const r = rng(seed)
    const pts = []
    const n = 7
    for (let i = 0; i < n; i++) {
        const a = Math.PI + (i / (n - 1)) * Math.PI
        const k = 0.8 + r() * 0.35
        pts.push([Math.cos(a) * s * k, Math.sin(a) * s * 0.75 * k])
    }
    inkPoly(ctx, pts, pal.stone, pal.ink, { line: 1.5 })
    ctx.save()
    ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.clip()
    ctx.fillStyle = pal.stoneLight
    ctx.beginPath(); ctx.ellipse(-s * 0.3, -s * 0.5, s * 0.55, s * 0.3, -0.3, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = pal.stoneDark
    ctx.beginPath(); ctx.ellipse(s * 0.5, -s * 0.05, s * 0.6, s * 0.35, 0, 0, Math.PI * 2); ctx.fill()
    if (r() < 0.5) { ctx.fillStyle = pal.moss; ctx.beginPath(); ctx.ellipse(-s * 0.1, -s * 0.72, s * 0.35, s * 0.14, 0, 0, Math.PI * 2); ctx.fill() }
    ctx.restore()
}

export function paintReeds(ctx, pal, s, seed) {
    const r = rng(seed)
    for (let i = 0; i < 7; i++) {
        const x = (r() - 0.5) * s * 1.2, h = s * (0.9 + r() * 0.8), bend = (r() - 0.5) * 6
        line(ctx, [[x, 0], [x + bend * 0.5, -h * 0.5], [x + bend, -h]], pal.leafDark, 1.6)
        if (r() < 0.55) {
            ctx.fillStyle = pal.trunk
            ctx.beginPath(); ctx.ellipse(x + bend, -h - 4, 2.4, 6, bend * 0.02, 0, Math.PI * 2); ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1; ctx.stroke()
        }
    }
    for (let i = 0; i < 4; i++) {
        const x = (r() - 0.5) * s
        line(ctx, [[x, 0], [x - 6 - r() * 5, -s * 0.5]], pal.leaf, 2.2)
    }
}

export function paintMushrooms(ctx, pal, s, seed) {
    const r = rng(seed)
    const caps = [[-s * 0.55, s * 0.9], [s * 0.2, s * 1.25], [s * 0.75, s * 0.7]]
    caps.forEach(([x, h], i) => {
        const w = s * (0.5 + r() * 0.15) * (i === 1 ? 1.25 : 1)
        inkPoly(ctx, [[x - w * 0.22, 0], [x - w * 0.18, -h], [x + w * 0.18, -h], [x + w * 0.22, 0]], pal.cloth, pal.ink, { line: 1.2 })
        ctx.beginPath(); ctx.moveTo(x - w, -h + 1); ctx.quadraticCurveTo(x - w * 0.9, -h - w * 0.95, x, -h - w * 0.95); ctx.quadraticCurveTo(x + w * 0.9, -h - w * 0.95, x + w, -h + 1); ctx.closePath()
        ctx.fillStyle = pal.mushroom; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
        ctx.fillStyle = "rgba(255,255,255,0.25)"
        ctx.beginPath(); ctx.ellipse(x - w * 0.35, -h - w * 0.55, w * 0.3, w * 0.16, -0.4, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = pal.cloth
        ;[[-0.45, -0.35], [0.15, -0.65], [0.5, -0.3]].forEach(([dx, dy]) => { ctx.beginPath(); ctx.arc(x + dx * w, -h + dy * w, w * 0.12, 0, Math.PI * 2); ctx.fill() })
    })
}

// A fallen log lying at `angle`, seen from above at a slant: a rounded body
// with bark lines, a lit top edge, and a cut end showing its rings.
export function paintLog(ctx, pal, s, angle = 0) {
    const len = s, thick = s * 0.3
    ctx.save()
    ctx.rotate(angle)
    ctx.scale(1, angle ? 0.8 : 1)
    const body = () => { ctx.beginPath(); ctx.moveTo(-len, -thick); ctx.lineTo(len, -thick); ctx.arc(len, 0, thick, -Math.PI / 2, Math.PI / 2); ctx.lineTo(-len, thick); ctx.arc(-len, 0, thick, Math.PI / 2, Math.PI * 1.5); ctx.closePath() }
    body()
    const g = ctx.createLinearGradient(0, -thick, 0, thick)
    g.addColorStop(0, pal.woodLight); g.addColorStop(0.35, pal.trunk); g.addColorStop(1, pal.trunkDark)
    ctx.fillStyle = g; ctx.fill()
    ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.stroke()
    ctx.save(); body(); ctx.clip()
    for (let i = -len; i < len; i += 7) line(ctx, [[i, -thick * 0.5], [i + 9, -thick * 0.35]], pal.trunkDark, 1, 0.55)
    for (let i = -len + 4; i < len; i += 11) line(ctx, [[i, thick * 0.25], [i + 12, thick * 0.4]], pal.ink, 0.9, 0.35)
    ctx.restore()
    ctx.beginPath(); ctx.ellipse(len, 0, thick * 0.55, thick * 0.95, 0, 0, Math.PI * 2)
    ctx.fillStyle = pal.woodLight; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
    ctx.strokeStyle = pal.woodDark; ctx.lineWidth = 0.8
    ;[0.6, 0.3].forEach((k) => { ctx.beginPath(); ctx.ellipse(len, 0, thick * 0.55 * k, thick * 0.95 * k, 0, 0, Math.PI * 2); ctx.stroke() })
    ctx.restore()
}

// A clump of wildflowers, flat enough to be drawn on the ground layer.
export function paintFlowerClump(ctx, pal, x, y, seed) {
    const r = rng(seed)
    const color = pal.flowers[Math.floor(r() * pal.flowers.length)]
    for (let i = 0; i < 6; i++) {
        const fx = x + (r() - 0.5) * 18, fy = y + (r() - 0.5) * 10
        line(ctx, [[fx, fy + 5], [fx, fy]], pal.tuft, 1)
        ctx.fillStyle = color
        for (let p = 0; p < 5; p++) {
            const a = (p / 5) * Math.PI * 2
            ctx.beginPath(); ctx.arc(fx + Math.cos(a) * 1.8, fy + Math.sin(a) * 1.8, 1.4, 0, Math.PI * 2); ctx.fill()
        }
        ctx.fillStyle = "#F2B640"
        ctx.beginPath(); ctx.arc(fx, fy, 0.9, 0, Math.PI * 2); ctx.fill()
    }
}

export function paintTuft(ctx, color, x, y, h) {
    ctx.strokeStyle = color
    ctx.beginPath()
    ctx.moveTo(x - 2.5, y); ctx.lineTo(x - 3.5, y - h * 0.8)
    ctx.moveTo(x, y); ctx.lineTo(x + 0.5, y - h)
    ctx.moveTo(x + 2.5, y); ctx.lineTo(x + 4, y - h * 0.75)
    ctx.stroke()
}
